import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { 
  Home, Plus, Calendar, Trash2, Edit, X, Save
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';

const BlockedDatesList = () => {
  const navigate = useNavigate();
  const [blockedDates, setBlockedDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    start_date: '',
    end_date: '',
    notes: ''
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchBlockedDates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/blocked-dates`, { 
        credentials: 'include' 
      });
      
      if (response.ok) {
        const data = await response.json();
        setBlockedDates(data);
      }
    } catch (error) {
      console.error('Error fetching blocked dates:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchBlockedDates();
  }, [fetchBlockedDates]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Auto-set end date when start date is set
    if (name === 'start_date' && value && !formData.end_date) {
      setFormData(prev => ({
        ...prev,
        start_date: value,
        end_date: value
      }));
      return;
    }
    
    // Validate end date is not before start date
    if (name === 'end_date' && formData.start_date && value) {
      if (new Date(value) < new Date(formData.start_date)) {
        toast.error('End date cannot be before start date');
        return;
      }
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({ start_date: '', end_date: '', notes: '' });
    setShowForm(false);
    setEditingId(null);
  };

  const handleEdit = (blocked) => {
    setFormData({
      start_date: blocked.start_date,
      end_date: blocked.end_date,
      notes: blocked.notes || ''
    });
    setEditingId(blocked.blocked_id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.start_date || !formData.end_date) {
      toast.error('Please fill in both start and end dates');
      return;
    }
    
    setSaving(true);
    
    try {
      const url = editingId 
        ? `${API_URL}/api/blocked-dates/${editingId}`
        : `${API_URL}/api/blocked-dates`;
      
      const response = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        toast.success(editingId ? 'Unavailable dates updated' : 'Unavailable dates added');
        resetForm();
        fetchBlockedDates();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save');
      }
    } catch (error) {
      console.error('Error saving blocked dates:', error);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      const response = await fetch(`${API_URL}/api/blocked-dates/${deleteId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Unavailable dates removed');
        fetchBlockedDates();
      } else {
        toast.error('Failed to delete');
      }
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Failed to delete');
    } finally {
      setDeleteId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#1a1f1a]">
      <header className="bg-[#2d5f3f] shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin')}
              className="text-[#f5f3ed] hover:bg-[#1a1f1a]/20"
              title="Home"
            >
              <Home className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-[#f5f3ed]">Unavailable Dates</h1>
              <p className="text-[#f5f3ed]/70 text-sm">Block dates when horsebox is not available</p>
            </div>
          </div>
          <Button
            onClick={() => setShowForm(true)}
            className="bg-[#c9a55a] hover:bg-[#b8944a] text-[#1a1f1a]"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Dates
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Add/Edit Form */}
        {showForm && (
          <div className="bg-[#2d5f3f]/30 rounded-xl p-6 border border-[#2d5f3f] mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-[#f5f3ed]">
                {editingId ? 'Edit Unavailable Dates' : 'Add Unavailable Dates'}
              </h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={resetForm}
                className="text-[#f5f3ed]/70 hover:text-[#f5f3ed]"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <Label className="text-[#f5f3ed]/70">Start Date *</Label>
                  <Input
                    type="date"
                    name="start_date"
                    value={formData.start_date}
                    onChange={handleInputChange}
                    className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
                    required
                  />
                </div>
                <div>
                  <Label className="text-[#f5f3ed]/70">End Date *</Label>
                  <Input
                    type="date"
                    name="end_date"
                    value={formData.end_date}
                    onChange={handleInputChange}
                    min={formData.start_date}
                    className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
                    required
                  />
                </div>
              </div>
              
              <div className="mb-4">
                <Label className="text-[#f5f3ed]/70">Notes</Label>
                <Textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Reason for unavailability (optional)"
                  className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
                  rows={2}
                />
              </div>
              
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={resetForm}
                  className="border-[#2d5f3f] text-[#f5f3ed]"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-[#c9a55a] hover:bg-[#b8944a] text-[#1a1f1a]"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : editingId ? 'Update' : 'Add'}
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#c9a55a]"></div>
          </div>
        ) : blockedDates.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="w-16 h-16 text-[#f5f3ed]/30 mx-auto mb-4" />
            <p className="text-[#f5f3ed]/50 mb-4">No unavailable dates set</p>
            <Button
              onClick={() => setShowForm(true)}
              className="bg-[#c9a55a] hover:bg-[#b8944a] text-[#1a1f1a]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add First Blocked Dates
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {blockedDates.map(blocked => (
              <div
                key={blocked.blocked_id}
                className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 flex items-center justify-between"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-[#f5f3ed] font-medium">
                      {formatDate(blocked.start_date)}
                      {blocked.start_date !== blocked.end_date && (
                        <> — {formatDate(blocked.end_date)}</>
                      )}
                    </p>
                    {blocked.notes && (
                      <p className="text-[#f5f3ed]/60 text-sm">{blocked.notes}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleEdit(blocked)}
                    className="text-blue-400 hover:bg-blue-500/20"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setDeleteId(blocked.blocked_id)}
                    className="text-red-400 hover:bg-red-500/20"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-[#1a1f1a] border-[#2d5f3f]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#f5f3ed]">Delete Unavailable Dates?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#f5f3ed]/70">
              This will remove these dates from the unavailable list. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#2d5f3f] text-[#f5f3ed]">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BlockedDatesList;
